import { z } from 'zod';
/** Per-tenant audit log actions (brief: Security controls → Audit and logging). */
export const AuditAction = z.enum([
    'tenant.created',
    'auth.signed_in',
    'auth.signed_out',
    'auth.mfa_changed',
    'member.invited',
    'member.role_changed',
    'member.removed',
    /** Invitations not yet accepted (docs/adr/0044): sent again, or revoked (the pending membership is removed). */
    'member.invite_resent',
    'member.invite_revoked',
    'features.updated',
    'settings.updated',
    'integration.connected',
    'integration.disconnected',
    /** A repository's webhook secret was replaced (docs/adr/0052). Never the secret. */
    'integration.secret_rotated',
    'export.requested',
    'tenant.deletion_requested',
    'breakglass.requested',
    'breakglass.approved',
    'breakglass.ended',
    // Break-glass support access (docs/adr/0051): who, when and what; never message bodies or task text.
    'breakglass.denied',
    'breakglass.revoked',
    'breakglass.cancelled',
    'breakglass.viewed',
    'breakglass.policy_changed',
    'share_link.created',
    'share_link.viewed',
    'share_link.revoked',
    'statuses.updated',
    'client.created',
    'project.created',
    /** People added to or removed from a project, and so its channel (docs/adr/0074). Counts only. */
    'project.members_changed',
    'channel.created',
    'channel.archived',
    'channel.chapi_changed',
    'channel.repo_activity_changed',
    'workspace.export_requested',
    'workspace.export_downloaded',
    // A person downloading their own data (docs/adr/0056).
    'account.data_export_requested',
    'account.data_export_downloaded',
    'workspace.deletion_scheduled',
    'workspace.deletion_cancelled',
    'import.started',
    'import.finished',
    'import.files_started',
    'import.files_finished',
    'import.legacy_applied',
    'support.email_unrouted',
    'support.address_changed',
    'support.plan_updated',
    'portal.signed_in',
    'portal.sign_in_link_sent',
    'portal.watcher_added',
    'support.watcher_added',
    'support.watcher_removed',
    /** Saved replies and merging (docs/adr/0102): ids only. */
    'support.canned_reply_created',
    'support.canned_reply_updated',
    'support.canned_reply_deleted',
    'support.ticket_merged',
    /** Undoing a merge (docs/adr/0103): ids and counts only. */
    'support.ticket_unmerged',
    'feedback.sent',
    'portal.ticket_created',
    /** Report and block (docs/adr/0039): the reason or the action, never the note or the message. */
    'report.submitted',
    'report.resolved',
    // Leaving and account deletion (docs/adr/0040-account-deletion.md).
    'member.left',
    'account.deletion_scheduled',
    'account.deletion_cancelled',
    'account.deleted',
    // Timesheets (docs/adr/0042): someone else's entry changed or removed; ids and fields, never the note.
    'time.entry_updated',
    'time.entry_deleted',
    // Billing (docs/adr/0044-billing-and-trials.md): plan, status and seat counts, never card or invoice details.
    'billing.checkout_started',
    'billing.details_updated',
    'billing.subscription_changed',
    'billing.payment_failed',
    'billing.payment_succeeded',
    /** A second live subscription (a webhook race) was cancelled at once and its payment refunded; ids and amounts only. */
    'billing.duplicate_subscription_cancelled',
    // Invoicing (docs/adr/0054): invoice ids and numbers, setting names and person ids; never amounts or client names.
    'invoicing.settings_updated',
    'invoicing.access_changed',
    'invoice.approved',
    'invoice.voided',
    'invoice.credited',
    'invoice.deleted',
    // Estimates and API tokens (docs/adr/0088): ids, numbers, statuses and token names; never amounts, lines or the token.
    'estimate.created',
    'estimate.updated',
    'api_token.created',
    'api_token.revoked',
    /** An owner or admin revoked someone else's token in Settings → Connectors (docs/adr/0098). Ids only. */
    'api_token.revoked_by_admin',
    // Payment schedules and assignment (docs/adr/0062): ids, numbers, counts and status names; never amounts or links.
    'invoicing.schedule_updated',
    'invoice.assigned',
    'invoice.suggestion_dismissed',
    'recorded_invoice.created',
    'recorded_invoice.updated',
    'recorded_invoice.status_changed',
    'recorded_invoice.deleted',
    /** A payment reminder sent to the client through Chapsi (docs/adr/0064): source, number and how many addresses. */
    'invoice.reminder_sent',
    // Single sign-on and SCIM (docs/adr/0055-sso-and-scim.md): ids, domains and switches, never secrets or tokens.
    'sso.connection_saved',
    'sso.connection_removed',
    'sso.domain_added',
    'sso.domain_verified',
    'sso.domain_removed',
    'sso.policy_updated',
    'sso.signed_in',
    'sso.member_provisioned',
    /** An owner confirmed it's them (fresh step-up) before changing single sign-on; the window is five minutes. */
    'sso.step_up',
    /**
     * Periodic re-checks of verified domains (worker, no actor): the TXT record went missing, the owners were warned,
     * it came back, or the grace period ended and the domain was marked unverified.
     */
    'sso.domain_recheck_failing',
    'sso.domain_recheck_warned',
    'sso.domain_recheck_recovered',
    'sso.domain_unverified',
    'scim.token_rotated',
    'scim.token_revoked',
    'scim.user_created',
    'scim.user_updated',
    'scim.user_deactivated',
    'scim.user_reactivated',
    'scim.group_changed',
    // Chapi and the work (docs/adr/0063): ids and counts only, never the brief, the update's text or addresses.
    'chapi.breakdown_confirmed',
    'chapi.client_update_sent',
    // Chapsi Showcase (docs/adr/0065): the set's id and name and a screen count; never file names or contents.
    'design_set.created',
    'design_set.deleted',
    // Showcase sharing (docs/adr/0068): ids, roles and switches; never comment text, addresses or tokens.
    'design_set.shared',
    'design_set.visibility_changed',
    'design_set.sharing_changed',
    'design_access.granted',
    'design_access.changed',
    'design_access.removed',
    'design_link.created',
    'design_link.changed',
    'design_link.revoked',
    'design_link.viewed',
    // Showcase exports (docs/adr/0071): what was asked for and downloaded (ids, kinds and switches only).
    'design_export.requested',
    'design_export.downloaded',
    'design_certificate.downloaded',
    // Showcase phase D (docs/adr/0069): versions started and published, and approvals (ids, numbers and flags only).
    'design_version.created',
    'design_version.published',
    'design_version.approved',
    // Showcase reply by email (docs/adr/0070): comment and reply ids and a reason code; never addresses, text or tokens.
    'design_comment.email_reply',
    'design_comment.email_reply_refused',
    // Showcase print (docs/adr/0072): the print-ready PDF of an approved version was downloaded (ids and counts only).
    'design_print_pdf.downloaded',
    // Conversation types and external people (docs/adr/0085): ids, types and counts; never names, addresses or messages.
    'conversation.created',
    'conversation.invited',
    'conversation.external_invited',
    'conversation.person_removed',
    // Channel membership (docs/adr/0099): who was added to or taken out of a channel, as user ids only.
    'channel.members_changed',
    // Project rules and roles (docs/adr/0109): rule, project and person ids, counts and flags; never names or templates.
    'rule.created',
    'rule.updated',
    'rule.deleted',
    'rule.switched',
    'project.role_changed',
]);
export const AuditEvent = z.object({
    id: z.uuid(),
    action: AuditAction,
    actorUserId: z.uuid().nullable(),
    targetType: z.string().nullable(),
    targetId: z.string().nullable(),
    /** Metadata only. Never message bodies, file contents or tokens. */
    metadata: z.record(z.string(), z.unknown()),
    createdAt: z.string(),
});
//# sourceMappingURL=audit.js.map